import { useEffect, useState } from 'react';
import { ConfigProvider, Descriptions, Input, Tooltip, message } from 'antd';
import { CloseOutlined, EditOutlined, SaveOutlined } from '@ant-design/icons';
import { useSelected } from '../../../../providers/selectedContext';
import { useTheme } from '../../../../providers/theme';
import { getColors } from '../../../utils/getColors';
import { updateSample } from '../../../../api/data';
import { UpdateSamplePayload } from '../../../../@types/samples/types';
import { Accordion } from '../../../../design-system/accordion';
import { useAppContext } from '../../../../providers/AppContext';
import { useNotificationAnchor } from '../../../../providers/NotificationCenter';
import AnalysisWaterOil from '../analysis/waterOil/analysis';
import WaterOilKrValues from '../krValues/waterOil/krValues';
import WaterOilRefinement from '../refinement/waterOil/refinement';
import { SonKroRegressionAnalysis, SwnKrwRegressionAnalysis } from '../../features';

const toNumberOrNull = (value: unknown): number | null => {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

type WaterOilVars = {
  Permeability: string;
  Porosity: string;
  Ko: string;
  Krw: string;
  Swi: string;
};

const SUFFIXES: Record<keyof WaterOilVars, string> = {
  Permeability: 'mD',
  Porosity: 'fraction',
  Ko: 'mD',
  Krw: 'fraction',
  Swi: 'fraction',
};

const WaterOilPanel = ({ activeTab }: { activeTab?: string }) => {
  const { data, updateData } = useAppContext();
  const { selectedInfo, setSelectedInfo } = useSelected();
  const { isDay } = useTheme();
  const colors = getColors(isDay);
  const sampleData = selectedInfo?.sample;
  const refinementAnchor = useNotificationAnchor(
    'special-core-water-oil-refinement'
  );

  const [edit, setEdit] = useState(false);
  const [vars, setVars] = useState<WaterOilVars>({
    Permeability: '',
    Porosity: '',
    Ko: '',
    Krw: '',
    Swi: '',
  });
  const [originalVars, setOriginalVars] = useState<WaterOilVars>(vars);

  useEffect(() => {
    if (!sampleData) return;
    const loaded: WaterOilVars = {
      Permeability: String(sampleData.input_sample_values.permeability ?? ''),
      Porosity: String(sampleData.input_sample_values.porosity ?? ''),
      Ko: String(sampleData.input_sample_values.ko ?? ''),
      Krw: String(sampleData.input_sample_values.krw ?? ''),
      Swi: String(sampleData.input_sample_values.swi ?? ''),
    };
    setVars(loaded);
    setOriginalVars(loaded);
    setEdit(false);
  }, [sampleData]);

  if (!sampleData) return null;

  const hasWaterOilLabCurves = (sampleData.input_sample_curves?.water_oil?.kro_lab?.length ?? 0) > 0;
  const hasWaterOilKrAnalysis = !!sampleData.k_rel_curves?.water_oil;
  const REQUIRED_FIELDS: Array<keyof WaterOilVars> = ['Permeability', 'Porosity'];

  const handleSave = async () => {
    try {
      const payload: UpdateSamplePayload = {
        sample_uid: sampleData.uid,
        input_sample_values: {
          permeability: toNumberOrNull(vars.Permeability),
          porosity: toNumberOrNull(vars.Porosity),
          ko: toNumberOrNull(vars.Ko),
          krw: toNumberOrNull(vars.Krw),
          swi: toNumberOrNull(vars.Swi),
        },
      };
      const response = await updateSample(payload);
      const updated = data.wellSamples.map(s =>
        s.uid === response.uid ? response : s
      );
      updateData({ wellSamples: updated });
      setSelectedInfo({ sample: response });
      setOriginalVars(vars);
      setEdit(false);
      message.success('Water-Oil variables saved.');
    } catch (error: any) {
      console.error('Water-Oil variables update error ===>', error);
      const apiMessage = error?.response?.data?.message || error?.message;
      message.error(apiMessage || 'Error saving Water-Oil variables. Please check your inputs and try again.');
    }
  };

  const editIcons = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }} onClick={e => e.stopPropagation()}>
      {edit ? (
        <>
          <Tooltip title="Save">
            <SaveOutlined
              onClick={handleSave}
              style={{ color: colors.colorPrimary, cursor: 'pointer', fontSize: '16px' }}
            />
          </Tooltip>
          <Tooltip title="Cancel">
            <CloseOutlined
              onClick={() => {
                setVars(originalVars);
                setEdit(false);
              }}
              style={{ color: '#ff4d4f', cursor: 'pointer', fontSize: '16px' }}
            />
          </Tooltip>
        </>
      ) : (
        <Tooltip title="Edit">
          <EditOutlined
            onClick={() => {
              setOriginalVars(vars);
              setEdit(true);
            }}
            style={{ color: colors.text, cursor: 'pointer', fontSize: '16px' }}
          />
        </Tooltip>
      )}
    </div>
  );

  return (
    <div>
      {/* Tab: Sample Measurements — Variables + Laboratory Data */}
      {(!activeTab || activeTab === 'sample-measurements') && (
        <>
          <Accordion defaultOpen="water-oil-vars">
            <Accordion.Item
              id="water-oil-vars"
              header={
                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', fontWeight: 'bold' }}>
                  <span>Water-Oil Variables</span>
                  {editIcons}
                </div>
              }
            >
            <ConfigProvider
              theme={{
                components: {
                  Descriptions: {
                    colorBgBase: colors.bg,
                    contentColor: colors.text,
                    labelBg: isDay ? '#fafafa' : '#090909',
                    colorBorder: colors.border,
                  },
                },
                token: {
                  colorText: colors.text,
                  colorBorder: colors.border,
                  colorBgBase: colors.bg,
                  colorPrimaryHover: colors.colorPrimary,
                  colorPrimary: colors.colorPrimary,
                },
              }}
            >
              <Descriptions
                layout="vertical"
                column={5}
                className={
                  isDay
                    ? 'special-core-descriptions-day'
                    : 'special-core-descriptions-night'
                }
              >
                {(Object.keys(vars) as Array<keyof WaterOilVars>).map(key => (
                  <Descriptions.Item
                    key={key}
                    label={
                      <span
                        style={{
                          color: isDay ? colors.text : '#FAFBFF',
                          paddingTop: '0.5rem',
                          paddingLeft: '0.5rem',
                        }}
                      >
                        {key}
                        <span style={{ opacity: 0.5, marginLeft: 4 }}>
                          ({SUFFIXES[key]})
                        </span>
                        {key === 'Swi' && (
                          <div style={{ fontSize: '0.7rem', color: '#b7950b', marginTop: 2 }}>
                            If empty, min(Sw) is used
                          </div>
                        )}
                      </span>
                    }
                    contentStyle={{ fontWeight: 'bold', padding: '0.2rem 0.5rem 0.5rem' }}
                  >
                    {edit ? (
                      <Input
                        value={vars[key]}
                        style={{ width: 100 }}
                        size="small"
                        onChange={e =>
                          setVars(prev => ({ ...prev, [key]: e.target.value }))
                        }
                      />
                    ) : (() => {
                      const isMissing = !vars[key] || vars[key] === '';
                      const isRequired = REQUIRED_FIELDS.includes(key);
                      return (
                        <span style={{ color: isRequired && isMissing ? '#ff4d4f' : 'inherit', fontWeight: isRequired && isMissing ? 600 : 'bold' }}>
                          {isMissing ? (isRequired ? '⚠ Required' : '--') : vars[key]}
                        </span>
                      );
                    })()}
                  </Descriptions.Item>
                ))}
              </Descriptions>
            </ConfigProvider>
            </Accordion.Item>
          </Accordion>

          <div style={{ marginTop: '1rem' }}>
            <AnalysisWaterOil />
          </div>
        </>
      )}

      {/* Tab: Refinement Variables — Find True Sor + Find True Krw */}
      {(!activeTab || activeTab === 'refinement-variables') && (
        hasWaterOilLabCurves ? (
          <>
            <div>
              <SonKroRegressionAnalysis />
            </div>
            <div style={{ marginTop: '1rem' }}>
              <SwnKrwRegressionAnalysis />
            </div>
          </>
        ) : activeTab === 'refinement-variables' ? (
          <div style={{ marginTop: '1rem', padding: '0.75rem 1rem', opacity: 0.65, fontSize: '0.875rem', border: '1px dashed', borderRadius: 6 }}>
            Enter lab data in <strong>Sample Measurements</strong> and click <strong>Calculate</strong> to enable refinement variables.
          </div>
        ) : null
      )}

      {/* Tab: Corey Exponents — No & Nw values */}
      {(!activeTab || activeTab === 'corey-exponents') && (
        hasWaterOilKrAnalysis ? (
          <WaterOilKrValues />
        ) : activeTab === 'corey-exponents' ? (
          <div style={{ marginTop: '1rem', padding: '0.75rem 1rem', opacity: 0.65, fontSize: '0.875rem', border: '1px dashed', borderRadius: 6 }}>
            Lab curves saved. Click <strong>Calculate</strong> in <strong>Sample Measurements</strong> to compute No &amp; Nw values.
          </div>
        ) : null
      )}

      {/* Tab: Refinement Curves — Refinement */}
      {(!activeTab || activeTab === 'refinement-curves') && (
        hasWaterOilKrAnalysis ? (
          <div ref={refinementAnchor} tabIndex={-1}>
            <WaterOilRefinement />
          </div>
        ) : activeTab === 'refinement-curves' ? (
          <div style={{ marginTop: '1rem', padding: '0.75rem 1rem', opacity: 0.65, fontSize: '0.875rem', border: '1px dashed', borderRadius: 6 }}>
            Lab curves saved. Click <strong>Calculate</strong> in <strong>Sample Measurements</strong> to enable refinement curves.
          </div>
        ) : null
      )}
    </div>
  );
};

export default WaterOilPanel;
